/**
 * Search-result media atoms (search-result-disclosure 03).
 *
 * Every preview is **fit** (`object-fit: contain`) so the whole picture is
 * visible — a cropped lid cannot be cross-checked against a box. Sizes are
 * fixed so rows stay aligned; the filmstrip scrolls instead of growing.
 */

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useT } from "../../shared/i18n";
import type { HitPhoto } from "./searchHits";

/** One square preview tile. */
export function HitThumb({
  photo,
  size = 48,
  radius = 1,
}: {
  photo: HitPhoto;
  size?: number;
  radius?: number;
}) {
  return (
    <Box
      component="img"
      src={photo.url}
      alt={photo.label}
      loading="lazy"
      sx={{
        width: size,
        height: size,
        borderRadius: radius,
        objectFit: "contain",
        flexShrink: 0,
        display: "block",
        bgcolor: "action.hover",
      }}
    />
  );
}

/** The level-3 cover: large, fit, never taller than the pane allows. */
export function HitCover({
  photo,
  maxHeight = 240,
}: {
  photo: HitPhoto | undefined;
  maxHeight?: number;
}) {
  const t = useT();
  if (!photo) {
    return (
      <Box
        sx={{
          width: "100%",
          aspectRatio: "4 / 3",
          maxHeight,
          borderRadius: 1,
          bgcolor: "action.hover",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t("searchNoPhotos")}
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={photo.url}
      alt={photo.label}
      sx={{
        width: "100%",
        aspectRatio: "4 / 3",
        maxHeight,
        objectFit: "contain",
        borderRadius: 1,
        display: "block",
        bgcolor: "action.hover",
      }}
    />
  );
}

/** Level 2: a bounded strip with a `+N` overflow chip. */
export function HitPhotoStrip({
  photos,
  max = 6,
  size = 64,
}: {
  photos: readonly HitPhoto[];
  max?: number;
  size?: number;
}) {
  if (photos.length === 0) return null;
  const shown = photos.slice(0, max);
  const overflow = photos.length - shown.length;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {shown.map((photo) => (
        <HitThumb key={photo.key} photo={photo} size={size} />
      ))}
      {overflow > 0 && (
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: 1,
            bgcolor: "action.hover",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 650 }}>
            +{overflow}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

/** Level 3: every photo, horizontally scrollable. */
export function HitFilmstrip({
  photos,
  size = 84,
}: {
  photos: readonly HitPhoto[];
  size?: number;
}) {
  const t = useT();
  if (photos.length === 0) return null;
  const count =
    photos.length === 1
      ? t("searchPhotoCountOne", { count: "1" })
      : t("searchPhotoCount", { count: String(photos.length) });
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {count}
      </Typography>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ overflowX: "auto", pb: 0.5, scrollbarWidth: "thin" }}
      >
        {photos.map((photo) => (
          <HitThumb key={photo.key} photo={photo} size={size} />
        ))}
      </Stack>
    </Box>
  );
}
